import type { TRssParsedItem } from '../models/rssDirectory'

/** Default caption below the article QR on the G1 story wall (pt-BR). */
export const RSS_G1_QR_CAPTION = 'Escaneie o QR para ler a matéria completa'

const RSS_PHOTO_PLACEHOLDER_LABELS: Record<string, string> = {
  'g1': 'g1',
  'uol': 'UOL',
  'ebc': 'AB',
  'folha': 'Folha',
  'cnn-brasil': 'CNN',
  'jornal-de-brasilia': 'JBr',
  'correio-braziliense': 'CB',
  'canaltech': 'CT'
}

function buildRssPhotoPlaceholderSvg(label: string): string {
  return `data:image/svg+xml,${
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360">'
      + '<rect width="640" height="360" fill="#26262c"/>'
      + `<text x="320" y="190" text-anchor="middle" font-family="system-ui,sans-serif" font-size="72" font-weight="800" fill="#9a9aa3">${label}</text>`
      + '</svg>'
    )}`
}

/** Gray placeholder when an RSS item has no image (label matches feed provider when known). */
export function rssPhotoPlaceholder(providerId?: string): string {
  const label = (providerId && RSS_PHOTO_PLACEHOLDER_LABELS[providerId]) || '…'
  return buildRssPhotoPlaceholderSvg(label)
}

export interface TRssStoryPhotoDisplay {
  style?: Record<string, string>
  width?: number
  height?: number
}

/** Layout hints for story-wall hero photos (native aspect ratio, no crop). */
export function resolveRssStoryPhotoDisplay(input: {
  item: Pick<TRssParsedItem, 'imageUrl' | 'imageWidth' | 'imageHeight'> | null | undefined
  usingPlaceholder: boolean
  naturalWidth?: number
  naturalHeight?: number
}): TRssStoryPhotoDisplay {
  if (input.usingPlaceholder) {
    return {
      style: { '--g1-photo-aspect-ratio': '16 / 9' },
      width: 640,
      height: 360
    }
  }

  const width = input.item?.imageWidth ?? input.naturalWidth
  const height = input.item?.imageHeight ?? input.naturalHeight
  if (width != null && height != null && width > 0 && height > 0) {
    return {
      style: { '--g1-photo-aspect-ratio': `${width} / ${height}` },
      width,
      height
    }
  }

  return {}
}

export function readRssStoryPhotoNaturalSize(img: HTMLImageElement): { width: number, height: number } | undefined {
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    return { width: img.naturalWidth, height: img.naturalHeight }
  }
  return undefined
}

/** True when the hero `<img>` has decoded pixels (load event or browser cache). */
export function isRssStoryPhotoRendered(img: HTMLImageElement | null | undefined): boolean {
  return Boolean(img?.complete && img.naturalWidth > 0)
}

/** @deprecated Use {@link rssPhotoPlaceholder} with provider id. */
export const RSS_G1_PHOTO_PLACEHOLDER = buildRssPhotoPlaceholderSvg('g1')

/** Section title for the story-wall top bar (from catalog label or RSS channel title). */
export function rssFeedTagFromLabel(label: string): string {
  const afterGt = label.match(/>\s*(.+)$/)
  if (afterGt?.[1]) {
    return afterGt[1].trim()
  }
  const withoutProvider = label.replace(
    /^(G1|UOL|Agência Brasil \(EBC\)|Folha de S\.Paulo|CNN Brasil|Jornal de Brasília|Correio Braziliense)\s*[—-]\s*/i,
    ''
  ).trim()
  if (withoutProvider) {
    return withoutProvider
  }
  return label.trim() || 'G1'
}
